import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Row } from "views/applications-id-report/components/Row";
import { useState } from "react";
import { Property } from "types/landlord/address";
import { SectionCard } from "shared/app/SectionCard";
import { formatPhone } from "shared/utils";
import { BrandingFields } from "shared/app/BrandingForm";
import { useFormTyped } from "shared/hooks";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { getErrorMessage } from "utils/error";
import * as yup from "yup";
import { Switch, SwitchSize } from "shared/components/forms/Switch";
import { useAuthContext } from "providers/Auth";
import { PropertyKind } from "shared/types";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { generatePrimaryPalette } from "shared/app/BrandingForm/components/ColorPicker/untils";

const sessionKey = `${Date.now()}-${crypto.randomUUID()}`;

interface EditBrandingFormProps {
  onClose: () => void;
  propertyId: number;
  showLandlordFields: boolean;
  initialValues: {
    enabled?: boolean;
    logo?: string;
    primary_color?: string;
    landlord_name?: string;
    landlord_phone?: string;
    palette?: Record<string, string>;
  };
}

const brandingValidationSchema = yup.object({
  enabled: yup.boolean(),
  logo: yup.string().optional(),
  primary_color: yup.string().when("enabled", {
    is: true,
    then: (schema) => schema.required("The color is required"),
    otherwise: (schema) => schema.optional()
  }),
  landlord_name: yup
    .string()
    .max(100, "Must be at most 100 characters")
    .optional(),
  landlord_phone: yup
    .string()
    .test(
      "phone-length",
      "Phone number must be exactly 10 characters",
      (value) => !value || value.length === 10
    )
    .optional(),
  palette: yup.object().optional()
});

const EditBrandingForm = ({
  onClose,
  propertyId,
  showLandlordFields,
  initialValues
}: EditBrandingFormProps) => {
  const queryClient = useQueryClient();
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const { Property, units } = useUnitsPropertiesDictionary();

  const { register, onSubmit, values, errors } = useFormTyped(
    initialValues,
    brandingValidationSchema
  );

  const { mutate: updateBranding, isPending } = useMutation({
    mutationFn: (vals: EditBrandingFormProps["initialValues"]) => {
      return addressApi.editProperty({
        propertyId,
        branding: {
          enabled: vals.enabled,
          logo: vals.logo,
          primary_color: vals.primary_color,
          landlord_name: vals.landlord_name,
          landlord_phone: vals.landlord_phone,
          palette: vals.palette
        }
      });
    },
    onSuccess: () => {
      toast.success("Branding updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  const handleSubmit = onSubmit({
    onSuccess: (vals) => updateBranding(vals),
    onError: () => {
      toast.error("Form validation failed");
    }
  });

  return (
    <SectionCard
      title="Branding"
      footer={
        <SaveCancelButtons
          onSave={(e) => handleSubmit(e)}
          onCancel={onClose}
          saveLoading={isPending}
          saveDisabled={isLogoUploading || Object.values(errors).some(Boolean)}
        />
      }
    >
      <div className="space-y-6">
        <div className="pb-6 border-b border-alpha-40 flex items-start gap-3">
          <Switch
            checked={values.enabled ?? false}
            onChange={(v) => register("enabled").onChange(v)}
            size={SwitchSize.BASE}
          />
          <div>
            <p className="text-sm font-medium text-content-700">
              {Property} level branding is{" "}
              {values.enabled ? "enabled" : "disabled"}
            </p>
            <p className="text-sm text-content-200">
              {values.enabled
                ? `${Property} and its ${units} can have their own branding`
                : "Property branding is defined at the partner level"}
            </p>
          </div>
        </div>

        {values.enabled && (
          <BrandingFields
            register={register}
            sessionKey={sessionKey}
            onUploadingChange={setIsLogoUploading}
            showLandlordFields={showLandlordFields}
          />
        )}
      </div>
    </SectionCard>
  );
};

export const BrandingSection = ({ property }: { property: Property }) => {
  const { type, partner } = useAuthContext();
  const [isEditing, setIsEditing] = useState(false);
  const { Property, units } = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();

  const showLandlordFields =
    (property.kind &&
      [PropertyKind.SingleFamily, PropertyKind.MultiFamily].includes(
        property.kind
      )) ||
    type.isPmsListingSyncEnabled;

  const partnerPrimaryColor = partner?.colors?.primary?.["500"];

  const { mutate: toggleEnabled, isPending: isToggling } = useMutation({
    mutationFn: (enabled: boolean) => {
      const needsColor = enabled && !property.branding?.primary_color;
      const defaultColor = partnerPrimaryColor || "#e6007e";
      return addressApi.editProperty({
        propertyId: property.id,
        branding: {
          enabled,
          ...(needsColor
            ? {
                primary_color: defaultColor,
                palette: generatePrimaryPalette(defaultColor)
              }
            : {})
        }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["properties", String(property.id)]
      });
    },
    onError: (error) => {
      toast.error(`Failed to update branding: ${getErrorMessage(error)}`);
    }
  });

  if (isEditing)
    return (
      <EditBrandingForm
        onClose={() => setIsEditing(false)}
        propertyId={property.id}
        showLandlordFields={showLandlordFields}
        initialValues={{
          enabled: property.branding?.enabled ?? false,
          logo:
            property.branding?.logo?.asset?.url ||
            (typeof property.branding?.logo === "string"
              ? property.branding.logo
              : undefined),
          primary_color:
            property.branding?.primary_color ||
            partner?.colors?.primary?.["500"],
          landlord_name: property.branding?.landlord_name,
          landlord_phone:
            property.branding?.landlord_phone?.replace(/\D/g, "") || undefined,
          palette:
            property.branding?.palette ||
            (partner?.colors?.primary?.["500"]
              ? generatePrimaryPalette(partner.colors.primary["500"])
              : undefined)
        }}
      />
    );

  const enabled = property.branding?.enabled ?? false;

  return (
    <SectionCard
      title="Branding"
      headerExtra={
        enabled ? (
          <Button
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            onClick={() => setIsEditing(true)}
          >
            Edit
          </Button>
        ) : undefined
      }
    >
      <div className={enabled ? "space-y-4" : ""}>
        <div
          className={`flex items-start gap-3${enabled ? " pb-4 border-b border-alpha-40" : ""}`}
        >
          <Switch
            checked={enabled}
            size={SwitchSize.BASE}
            disabled={isToggling}
            onChange={(v) => toggleEnabled(v)}
          />
          <div>
            <p className="text-sm font-medium text-content-700">
              {Property} level branding is {enabled ? "enabled" : "disabled"}
            </p>
            <p className="text-sm text-content-200">
              {enabled
                ? `${Property} and its ${units} can have their own branding`
                : "Property branding is defined at the partner level"}
            </p>
          </div>
        </div>

        {enabled && (
          <div className="space-y-4">
            <Row
              title="Primary color"
              value={property.branding?.primary_color}
            />
            {showLandlordFields && (
              <>
                <Row
                  title="Landlord name"
                  value={property.branding?.landlord_name}
                />
                <Row
                  title="Landlord phone"
                  value={formatPhone(property.branding?.landlord_phone || "")}
                />
              </>
            )}
          </div>
        )}
      </div>
    </SectionCard>
  );
};
